/**
 * Whose address this browser is at — `docs/identity-and-sign-in.md` §5.3.
 *
 * `GET /host` takes no parameter, so this hook cannot ask about a company: it
 * describes the host the page was loaded from and nothing else. That is what
 * lets the sign-in page carry Keystone's name and mark with no session, and
 * what stops the same endpoint being a directory of Rowted's customers.
 *
 * Fetched once per page load and cached in the module. A host does not change
 * while somebody is looking at it, and every screen that wants it (sign-in, the
 * crew door, the frame's environment chip) would otherwise ask again.
 */
import { useEffect, useState } from 'react'

export interface HostTenantInfo {
  id: string
  slug: string
  name: string
  displayName: string
  hasMark: boolean
  brandColor: string | null
}

export interface HostInfo {
  rootHost: string
  rootUrl: string
  /** Null on the front door — `app.rowted.com` itself. */
  tenant: HostTenantInfo | null
}

/**
 * What the page knows before the answer arrives.
 *
 * `unknown` rather than "the front door", because the two are drawn
 * differently: a sign-in page that assumed the front door for a moment would
 * flash the generic head before Keystone's name replaced it.
 */
export type HostState =
  | { state: 'loading' }
  | { state: 'ready'; host: HostInfo }
  /**
   * A subdomain that names nobody. Not an error to retry — it is the answer,
   * and §5.2 says what to draw for it: "There's no company at this address",
   * never a redirect to some other company's app.
   */
  | { state: 'nobody'; rootHost: string | null }

let cached: HostState | null = null
const waiting: ((state: HostState) => void)[] = []

/** Exported for the tests, which need each case from a clean slate. */
export function forgetHost(): void {
  cached = null
  waiting.length = 0
}

async function load(): Promise<HostState> {
  /*
   * Plain `fetch`, not `lib/api.ts`'s `request`. That helper adds the CSRF
   * header, the scope parameter and the 401 handling that sends somebody to
   * sign in — and this is the call the sign-in page itself makes, so a 401
   * redirect loop is exactly what it must not do.
   */
  const response = await fetch('/api/v1/host', { headers: { accept: 'application/json' } })
  if (!response.ok) {
    return { state: 'nobody', rootHost: null }
  }
  const host = await response.json() as HostInfo
  /*
   * The server answers `tenant: null` for the front door. A subdomain that
   * resolved to nobody also answers `tenant: null` — with the difference that
   * the browser is not *at* the front door, which is the only place the client
   * can tell the two apart.
   */
  const atRoot = window.location.host.toLowerCase() === host.rootHost.toLowerCase()
  if (!host.tenant && !atRoot) return { state: 'nobody', rootHost: host.rootHost }
  return { state: 'ready', host }
}

export function useHost(): HostState {
  const [state, setState] = useState<HostState>(cached ?? { state: 'loading' })

  useEffect(() => {
    if (cached) { setState(cached); return }
    let live = true
    waiting.push((answer) => { if (live) setState(answer) })
    if (waiting.length === 1) {
      void load()
        .catch((): HostState => ({ state: 'nobody', rootHost: null }))
        .then((answer) => {
          cached = answer
          for (const tell of waiting.splice(0)) tell(answer)
        })
    }
    return () => { live = false }
  }, [])

  return state
}
